<template>
  <div ref="root" class="camera-stream" :class="{ 'camera-stream--maximized': maximized }">
    <!-- El <iframe> es SIEMPRE el primer hijo. El header/PTZ del modo
         maximizado se montan DESPUÉS (position: absolute), así el iframe nunca
         cambia de índice en el DOM al togglear maximizado y el stream NO se
         recarga (un reorden de hermanos antes del iframe lo recargaría). -->
    <iframe
      v-if="shouldLoad"
      :key="'camera-stream-iframe-' + reloadCount"
      :src="url"
      class="camera-stream-frame"
      frameborder="0"
      allow="autoplay"
      allowfullscreen
    ></iframe>
    <div v-else class="camera-stream-placeholder" @click="forceLoad">
      <v-icon small color="rgba(255,255,255,0.4)">mdi-play-circle-outline</v-icon>
    </div>
    <!-- Header del modo maximizado (mismo look que el overlay anterior).
         position: absolute → va DESPUÉS del iframe para no reordenarlo. -->
    <div v-if="maximized" class="camera-stream-max-header">
      <div class="camera-stream-max-title">
        <v-icon small color="rgba(255,255,255,0.7)" class="mr-2">mdi-television-play</v-icon>
        <span>{{ (camera && (camera.name || camera.title)) || 'Cámara' }}</span>
      </div>
      <div class="camera-stream-max-actions">
        <v-btn icon small v-auto-tooltip="'Recargar cámara'" @click.stop="onReload">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <v-btn icon small v-auto-tooltip="'Cerrar (ESC)'" @click.stop="closeMaximized">
          <v-icon>mdi-fullscreen-exit</v-icon>
        </v-btn>
      </div>
    </div>
    <!-- PTZ en modo maximizado (paridad con el overlay anterior). Solo si la
         cámara es PTZ y tenemos el uniqueId del dispositivo padre. -->
    <div v-if="maximized && camera && camera.isPtz && ptzUniqueId && puedePtz" class="camera-stream-ptz">
      <div class="ptz-dpad">
        <div class="d-flex justify-center">
          <v-btn color="secondary" fab small dark @click.stop="ptzMove('moveup')"><v-icon>mdi-chevron-up</v-icon></v-btn>
        </div>
        <div class="d-flex justify-space-between">
          <v-btn color="secondary" fab small dark @click.stop="ptzMove('moveleft')"><v-icon>mdi-chevron-left</v-icon></v-btn>
          <v-btn color="secondary" fab small dark @click.stop="ptzMove('moveright')"><v-icon>mdi-chevron-right</v-icon></v-btn>
        </div>
        <div class="d-flex justify-center">
          <v-btn color="secondary" fab small dark @click.stop="ptzMove('movedown')"><v-icon>mdi-chevron-down</v-icon></v-btn>
        </div>
      </div>
      <div class="ptz-zoom">
        <v-btn color="secondary" fab small dark @click.stop="ptzMove('zoomin')"><v-icon>mdi-magnify-plus</v-icon></v-btn>
        <v-btn color="secondary" fab small dark @click.stop="ptzMove('zoomout')"><v-icon>mdi-magnify-minus</v-icon></v-btn>
      </div>
    </div>
    <!-- Fallo PTZ in-situ: píldora discreta sobre el video (el éxito no
         necesita retro — la cámara se mueve). Se desvanece sola. -->
    <div v-if="ptzError" class="ptz-error-flash">
      <v-icon x-small color="var(--kln-alert-danger)">mdi-lan-disconnect</v-icon>
      <span>Sin respuesta de la cámara</span>
    </div>
    <v-btn
      icon
      x-small
      class="camera-stream-reload-btn"
      data-tip="Recargar cámara"
      @click.stop="onReload"
    >
      <v-icon small>mdi-refresh</v-icon>
    </v-btn>
    <v-btn
      v-if="fullscreenEnabled && camera"
      icon
      x-small
      class="camera-stream-focus-btn"
      data-tip="Ver en pantalla completa"
      @click.stop="onFocus"
    >
      <v-icon small>mdi-fullscreen</v-icon>
    </v-btn>
  </div>
</template>

<script>
/**
 * Camera stream via iframe.
 *
 * The /api/v2/video/stream/ endpoint returns an HTML page with its own
 * embedded video player. This is how the original map (CameraWindow.vue)
 * renders all cameras — via <iframe>.
 *
 * To avoid loading 4+ iframes simultaneously (expensive), cameras can
 * be set to lazy load with autoplay=false (click play button to load).
 */
import { sendPtzCommand } from '@/components/map-v2/utils/ptz-service'
import { canUsePtz } from '@/util/user-permissions'

export default {
  name: 'CameraStream',

  props: {
    url: { type: String, required: true },
    autoplay: { type: Boolean, default: true },
    // Objeto de cámara completo, usado para disparar el fullscreen
    // individual. Si se omite, el botón de fullscreen no se muestra.
    camera: { type: Object, default: null },
    // Permite deshabilitar el botón de fullscreen desde el padre
    fullscreenEnabled: { type: Boolean, default: true },
    // uniqueId del dispositivo padre (PMI) para los comandos PTZ en el modo
    // maximizado. El backend espera { uniqueid, camname: camera.devId }.
    // Si falta (p.ej. sub-cámaras), no se muestran controles PTZ.
    ptzUniqueId: { type: String, default: '' },
  },

  data: function() {
    return {
      manualLoad: false,
      // Counter para forzar re-mount del iframe via :key dinamico
      // cuando el feed se congela y el usuario clickea reload.
      reloadCount: 0,
      // Modo maximizado (pantalla completa individual). Promueve ESTE
      // contenedor a position:fixed (CSS-promote) reusando el mismo iframe,
      // así el stream NO se recarga. Emite 'maximize-change' para que el
      // padre (MapDrawer) neutralice el backdrop-filter de .map-drawer.
      maximized: false,
      // true mientras se muestra la píldora de fallo PTZ in-situ.
      ptzError: false,
    }
  },

  computed: {
    puedePtz() {
      return canUsePtz(this.$store.getters['auth/getUser'])
    },
    shouldLoad: function() {
      return this.autoplay || this.manualLoad
    },
  },

  watch: {
    maximized: function(val) {
      // ESC en fase CAPTURA (true) para cerrar la cámara maximizada.
      // this._onMaxKeydown es referencia estable (created) → idempotente.
      if (val) {
        document.addEventListener('keydown', this._onMaxKeydown, true)
      } else {
        document.removeEventListener('keydown', this._onMaxKeydown, true)
      }
    },
  },

  created: function() {
    this._onMaxKeydown = this._onMaxKeydownImpl.bind(this)
    // Estado no reactivo para el re-init por resize.
    this._lastRenderWidth = 0
    this._resizeTimer = null
    this._ro = null
  },

  mounted: function() {
    // El player embebido en el iframe (HikCentral jsPlugin / mediamtx / mpeg1)
    // fija la resolución de su canvas AL CARGAR, leyendo el tamaño del
    // contenedor una sola vez. Al agrandar la celda con CSS el iframe se
    // estira pero el player no se entera → imagen borrosa hasta recargar.
    // Observamos el tamaño y, si CRECE de forma significativa (debounced),
    // remontamos el iframe para que el player re-lea el nuevo tamaño y suba
    // resolución. Encoger no remonta (downscale ya se ve nítido → sin
    // parpadeo innecesario).
    if (typeof ResizeObserver === 'undefined' || !this.$refs.root) return
    var self = this
    this._ro = new ResizeObserver(function(entries) {
      var rect = entries && entries[0] && entries[0].contentRect
      self._onResize(rect ? rect.width : 0)
    })
    this._ro.observe(this.$refs.root)
  },

  methods: {
    _onResize: function(w) {
      if (!w || !this.shouldLoad) return
      // Maximizado = CSS-promote deliberado SIN recarga (instantáneo). El salto
      // de tamaño celda→fullscreen NO es "creció el grid": ignorarlo por
      // completo y NO tocar la línea base (sigue siendo el ancho de la celda,
      // así el restore tampoco dispara reload). Sin este guard, el observer
      // remontaba el iframe ~500ms después de maximizar → pantalla negra
      // mientras el player renegocia el stream (regresión de a62a52a1 sobre
      // el diseño sin-recarga de b48a87ca).
      if (this.maximized) return
      // Primera medición: fija la línea base (el iframe acaba de montar a
      // este tamaño), sin remontar.
      if (!this._lastRenderWidth) { this._lastRenderWidth = w; return }
      var self = this
      if (this._resizeTimer) clearTimeout(this._resizeTimer)
      this._resizeTimer = setTimeout(function() {
        self._resizeTimer = null
        // Guard de carrera: un resize real agendó este debounce y el operador
        // maximizó dentro de los 500ms — al medir ahora veríamos el ancho de
        // fullscreen. No recargar.
        if (self.maximized) return
        var cur = self.$refs.root ? self.$refs.root.clientWidth : 0
        if (!cur || !self.shouldLoad) return
        if (cur > self._lastRenderWidth * 1.2) {
          // Creció >20%: re-init para subir resolución.
          self._lastRenderWidth = cur
          self.reloadCount += 1
        } else if (cur < self._lastRenderWidth) {
          // Encogió: sin remount, pero bajamos la base para que un
          // crecimiento posterior vuelva a disparar.
          self._lastRenderWidth = cur
        }
      }, 500)
    },
    forceLoad: function() {
      this.manualLoad = true
    },
    onFocus: function() {
      if (!this.camera) return
      // Maximiza reusando el mismo iframe (CSS-promote): no se monta overlay
      // ni iframe nuevo, por lo que el stream no se recarga.
      this.maximized = true
      this.$emit('maximize-change', true)
    },
    closeMaximized: function() {
      this.maximized = false
      this.$emit('maximize-change', false)
    },
    onReload: function() {
      // Incrementar reloadCount fuerza re-mount del iframe via :key
      // dinamico, recargando el feed cuando se congela.
      this.reloadCount += 1
    },
    _onMaxKeydownImpl: function(e) {
      if (e.key !== 'Escape') return
      if (!this.maximized) return
      e.stopImmediatePropagation()
      e.preventDefault()
      this.closeMaximized()
    },
    ptzMove: function(action) {
      var self = this
      var camName = (this.camera && this.camera.devId) || ''
      sendPtzCommand(action, this.ptzUniqueId, camName).then(function(r) {
        if (r && !r.ok) self._flashPtzError()
      })
    },
    // Píldora in-situ "sin respuesta" sobre el video. Se auto-oculta
    // (~2.6s, en sincronía con la animación CSS).
    _flashPtzError: function() {
      if (this._ptzErrorTimer) clearTimeout(this._ptzErrorTimer)
      this.ptzError = true
      var self = this
      this._ptzErrorTimer = setTimeout(function() {
        self.ptzError = false
        self._ptzErrorTimer = null
      }, 2600)
    },
  },

  beforeDestroy: function() {
    // Si el componente muere maximizado (ej. cambio de selección/ruta),
    // avisar al padre para que restaure el backdrop-filter de .map-drawer.
    if (this.maximized) {
      this.$emit('maximize-change', false)
    }
    document.removeEventListener('keydown', this._onMaxKeydown, true)
    if (this._ro) { this._ro.disconnect(); this._ro = null }
    if (this._resizeTimer) { clearTimeout(this._resizeTimer); this._resizeTimer = null }
    if (this._ptzErrorTimer) { clearTimeout(this._ptzErrorTimer); this._ptzErrorTimer = null }
    // Kill iframe to stop its internal player
    this.manualLoad = false
  },
}
</script>

<style scoped>
.camera-stream {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 4px;
  overflow: hidden;
}

.camera-stream-frame {
  width: 100%;
  height: 100%;
  border: none;
}

.camera-stream-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.camera-stream-placeholder:hover {
  background: rgba(255, 255, 255, 0.05);
}

.camera-stream-focus-btn {
  position: absolute !important;
  top: 4px;
  right: 4px;
  opacity: 0;
  transition: opacity 0.15s;
  background: rgba(0, 0, 0, 0.55) !important;
  z-index: 3;
}

.camera-stream:hover .camera-stream-focus-btn {
  opacity: 1;
}

.camera-stream-reload-btn {
  position: absolute !important;
  top: 4px;
  right: 28px;
  opacity: 0;
  transition: opacity 0.15s;
  background: rgba(0, 0, 0, 0.55) !important;
  z-index: 3;
}

.camera-stream:hover .camera-stream-reload-btn {
  opacity: 1;
}

/* ─── Modo maximizado (CSS-promote, sin recarga) ───────────────────
   Promueve ESTE contenedor a fixed reusando el mismo iframe. El
   backdrop-filter de .map-drawer (ancestro) se neutraliza desde el padre
   vía la clase .map-drawer--maximized (emit 'maximize-change'). */
.camera-stream--maximized {
  position: fixed;
  inset: 0;
  z-index: 9500;
  width: auto;
  aspect-ratio: auto;
  border-radius: 0;
  background: rgba(0, 0, 0, 0.96);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Letterbox 16:9 centrado debajo del header (~44px) + padding. */
.camera-stream--maximized .camera-stream-frame {
  width: 100%;
  height: auto;
  max-width: calc((100vh - 92px) * 16 / 9);
  max-height: calc(100vh - 92px);
  aspect-ratio: 16 / 9;
}

/* Los botones de esquina se ocultan: el header maximizado los reemplaza. */
.camera-stream--maximized .camera-stream-reload-btn,
.camera-stream--maximized .camera-stream-focus-btn {
  display: none !important;
}

.camera-stream-max-header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 18px;
  background: rgba(10, 14, 26, 0.85);
  border-bottom: 1px solid var(--kln-border-dim, rgba(255, 255, 255, 0.12));
}

.camera-stream-max-title {
  display: flex;
  align-items: center;
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--kln-text, #fff);
  letter-spacing: 0.02em;
}

.camera-stream-max-title span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.camera-stream-max-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.camera-stream-max-actions .v-btn {
  color: var(--kln-text-muted, rgba(255, 255, 255, 0.7)) !important;
}

.camera-stream-max-actions .v-btn:hover {
  color: var(--kln-text, #fff) !important;
  background: var(--kln-border-dim, rgba(255, 255, 255, 0.12)) !important;
}

/* PTZ en el modo maximizado — esquina inferior derecha (igual que el
   overlay anterior). z-index sobre el iframe del stream. */
.camera-stream-ptz {
  position: absolute;
  bottom: 40px;
  right: 40px;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: rgba(10, 14, 26, 0.7);
  padding: 16px;
  border-radius: 12px;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.camera-stream-ptz .ptz-dpad {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.camera-stream-ptz .ptz-zoom {
  display: flex;
  justify-content: center;
  gap: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 12px;
}

/* Indicador in-situ de fallo PTZ: píldora discreta sobre el video que se
   desvanece sola (~2.6s, sincronizado con el timer del componente).
   Arriba (bajo el header del maximizado — el PTZ de este componente solo
   existe maximizado) para NO tapar el dpad PTZ de abajo. */
.ptz-error-flash {
  position: absolute;
  top: 56px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 6;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(10, 10, 15, 0.82);
  border: 1px solid var(--kln-alert-danger);
  color: rgba(255, 255, 255, 0.92);
  font-size: 11px;
  white-space: nowrap;
  pointer-events: none;
  animation: ptz-error-cycle 2.6s ease forwards;
}

.ptz-error-flash .v-icon {
  animation: ptz-error-shake 0.4s ease 0.15s;
}

@keyframes ptz-error-cycle {
  0% { opacity: 0; }
  8% { opacity: 1; }
  82% { opacity: 1; }
  100% { opacity: 0; }
}

@keyframes ptz-error-shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-3px); }
  75% { transform: translateX(3px); }
}
</style>
